// UdcPage.js — Сторінка "Пошук за класифікатором УДК"
//
// Навігація по індексу U= реального каталогу ІРБІС через udc.php (readTerms).
// Верхній рівень: класи 0–9 з хардкодованими мітками.
// Глибше: динамічно підвантажуються вузли наступного рівня.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';
import { t } from '../i18n';
import './UdcPage.css';

const UDC_QUERY_KEY = 'irbis_udc_queries';
const KW_PER_PAGE = 10;
const UDC_STATE_KEY = 'irbis_udc_state';

// Збережений стан УДК-навігатора (stack/searchInput/searchMode/kwLogic/kwStem) —
// єдина точка читання sessionStorage замість окремого JSON.parse під кожне поле.
function readUdcState() {
  try { return JSON.parse(sessionStorage.getItem(UDC_STATE_KEY) || '{}'); }
  catch { return {}; }
}

// Реактивна перевірка вузького екрана (сесія 2026-08-24) — для короткого
// плейсхолдера .udc-search-input: довгий текст з прикладом коду («Введіть
// код УДК, наприклад: 37 або 51.01») не влазить і обрізається браузером на
// вузьких телефонах (~360-390px) — плейсхолдер <input> не переноситься на
// новий рядок жодним CSS, це обмеження самого HTML-елемента (на відміну
// від <textarea>). 480px — емпіричний поріг: саме на цій ширині рядок
// перестає влазити з урахуванням падінгів поля та кнопки пошуку.
function useIsNarrowMobile() {
  const NARROW_MQ = '(max-width: 480px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW_MQ);
    const handler = e => setNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return narrow;
}

const UDC_CLASSES = {
  '0': { icon: 'fa-solid fa-microchip',       color: '#3b82f6' },
  '1': { icon: 'fa-solid fa-brain',            color: '#8b5cf6' },
  '2': { icon: 'fa-solid fa-church',           color: '#6366f1' },
  '3': { icon: 'fa-solid fa-scale-balanced',   color: '#0ea5e9' },
  '5': { icon: 'fa-solid fa-flask-vial',       color: '#10b981' },
  '6': { icon: 'fa-solid fa-gears',            color: '#f59e0b' },
  '7': { icon: 'fa-solid fa-palette',          color: '#ec4899' },
  '8': { icon: 'fa-solid fa-book-open',        color: '#ef4444' },
  '9': { icon: 'fa-solid fa-globe',            color: '#84cc16' },
};

export default function UdcPage({ db, lang, onSearch, onKeywordSearch, onBack, backLabel }) {
  // Одне читання sessionStorage замість окремого JSON.parse під кожне поле нижче
  // (useState-ініціалізатори все одно виконуються лише при першому рендері).
  const savedUdcState = readUdcState();
  const isNarrowMobile = useIsNarrowMobile();
  // stack: масив { code, label } — шлях від кореня до поточного вузла
  const [stack, setStack] = useState(() => savedUdcState.stack || []);
  const [nodes,    setNodes]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
const [searchInput, setSearchInput] = useState(() => savedUdcState.searchInput || '');
const [searchMode, setSearchMode] = useState(() => savedUdcState.searchMode || 'code');
const [searchModeOpen, setSearchModeOpen] = useState(false);
const [searchResults,  setSearchResults]  = useState(null);
const [searchLoading,  setSearchLoading]  = useState(false);
const abortRef         = useRef(null);
const modeDropdownRef  = useRef(null);
const searchBarRef     = useRef(null);
const optionsRef       = useRef(null);
const [showHistory,  setShowHistory]  = useState(false);
const [udcHistory,   setUdcHistory]   = useState([]);
const [selectedCodes, setSelectedCodes] = useState(new Set());
const [kwPage, setKwPage] = useState(1);
const [kwJumpInput, setKwJumpInput] = useState('');
const [kwLogic, setKwLogic] = useState(() => savedUdcState.kwLogic || 'or');
const [kwStem, setKwStem] = useState(() => savedUdcState.kwStem !== false);
const [kwOptionsOpen, setKwOptionsOpen] = useState(false);

const currentCode = stack.length > 0 ? stack[stack.length - 1].code : '';
const kwTotalPages = searchResults ? Math.ceil(searchResults.length / KW_PER_PAGE) : 0;
const kwDisplayed  = searchResults
  ? searchResults.slice((kwPage - 1) * KW_PER_PAGE, kwPage * KW_PER_PAGE)
  : [];
const showingKwResults = searchResults !== null || searchLoading;


  // ── Завантаження вузлів для поточного коду ────────────────
  const loadNodes = useCallback((prefix) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError('');

    const url = `${API_URL}/udc.php?db=${encodeURIComponent(db)}&prefix=${encodeURIComponent(prefix)}`;
    fetch(url, { signal: ctrl.signal })
      .then(r => r.json())
      .then(data => {
        setNodes(data.nodes || []);
        setTotal(data.total || 0);
      })
      .catch(e => {
        if (e.name !== 'AbortError') setError(t(lang, 'udc_error'));
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, lang]);

  // Завантажити при монтуванні і при зміні бази
  useEffect(() => {
    loadNodes(currentCode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db]);

const doKeywordSearch = (val, log = true) => {
  setKwPage(1);
  setSearchLoading(true);
  setSearchResults(null);
  fetch(`${API_URL}/udc.php?action=search&q=${encodeURIComponent(val)}&logic=${kwLogic}&stem=${kwStem ? '1' : '0'}`)
    .then(r => r.json())
    .then(data => {
      const results = data.results || [];
      setSearchResults(results);
      if (log && onKeywordSearch) onKeywordSearch(val, results.length, { logic: kwLogic, stem: kwStem });
    })
    .catch(() => setSearchResults([]))
    .finally(() => setSearchLoading(false));
};

  // Зберегти стан УДК у sessionStorage для відновлення після перезавантаження
useEffect(() => {
  try {
    sessionStorage.setItem(UDC_STATE_KEY, JSON.stringify({
      stack, searchMode, searchInput, kwLogic, kwStem,
    }));
  } catch {}
}, [stack, searchMode, searchInput, kwLogic, kwStem]);

// Відновити пошук за ключовими словами після перезавантаження
useEffect(() => {
  const s = readUdcState();
  if (s.searchMode === 'keyword' && s.searchInput?.trim()) {
    doKeywordSearch(s.searchInput.trim(), false);
  }
}, []); // eslint-disable-line react-hooks/exhaustive-deps

// ── Закриття дропдауну при кліку поза ним ─────────────────
  useEffect(() => {
const handler = (e) => {
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(e.target))
        setSearchModeOpen(false);
      if (searchBarRef.current && !searchBarRef.current.contains(e.target))
        setShowHistory(false);
      if (optionsRef.current && !optionsRef.current.contains(e.target))
        setKwOptionsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Перехід вглиб ─────────────────────────────────────────
  const navigateTo = (code, label) => {
    const newStack = [...stack, { code, label }];
    setStack(newStack);
    loadNodes(code);
  };

  // ── Перехід на рівень хлібних крихт ──────────────────────
  const navigateToBreadcrumb = (idx) => {
    if (idx < 0) {
      setStack([]);
      loadNodes('');
    } else {
      const newStack = stack.slice(0, idx + 1);
      setStack(newStack);
      loadNodes(stack[idx].code);
    }
    setSearchResults(null);
    setSearchInput('');
  };

  // ── Пошук за кодом з поточного вузла ─────────────────────
  const handleNodeSearch = (code, seeAlso = false) => {
    onSearch(code, seeAlso);
  };

  // ── Перемикання вибору вузла ──────────────────────────────
  const toggleCode = useCallback((code) => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  // ── Пошук за всіма вибраними вузлами ─────────────────────
  const handleSelectedSearch = useCallback(() => {
    if (selectedCodes.size === 0) return;
    onSearch(Array.from(selectedCodes).join(','));
    setSelectedCodes(new Set());
  }, [selectedCodes, onSearch]);

  const handleKwJump = () => {
    const p = parseInt(kwJumpInput, 10);
    if (p >= 1 && p <= kwTotalPages) { setKwPage(p); setKwJumpInput(''); }
  };

  const getKwPages = () => {
    if (kwTotalPages <= 7) return Array.from({ length: kwTotalPages }, (_, i) => i + 1);
    const s = new Set([1, kwTotalPages, kwPage]);
    if (kwPage > 1) s.add(kwPage - 1);
    if (kwPage < kwTotalPages) s.add(kwPage + 1);
    const sorted = [...s].sort((a, b) => a - b);
    const res = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) res.push('...');
      res.push(sorted[i]);
    }
    return res;
  };

 // ── Завантажити/відфільтрувати локальну історію УДК ───────
  const loadUdcHistory = useCallback((inputVal = '') => {
    try {
      const all = JSON.parse(localStorage.getItem(UDC_QUERY_KEY) || '[]');
      const q = inputVal.trim().toLowerCase();
      const filtered = all
        .filter(r => r.query && (!q || r.query.toLowerCase().includes(q)))
        .filter((r, i, arr) => arr.findIndex(x => x.query === r.query) === i)
        .slice(0, 8);
      setUdcHistory(filtered);
    } catch { setUdcHistory([]); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Зберегти запит в локальну історію УДК ─────────────────
  const saveUdcQuery = (val, mode) => {
    if (!val.trim()) return;
    try {
      const entry = {
        query: val.trim(), mode,
        date: new Date().toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' }),
      };
      const existing = JSON.parse(localStorage.getItem(UDC_QUERY_KEY) || '[]');
      const deduped  = [entry, ...existing.filter(r => r.query !== entry.query)].slice(0, 20);
      localStorage.setItem(UDC_QUERY_KEY, JSON.stringify(deduped));
    } catch {}
  };

  // ── Видалити один запит з історії ─────────────────────────
  const deleteUdcHistoryItem = (e, q) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const existing = JSON.parse(localStorage.getItem(UDC_QUERY_KEY) || '[]');
      localStorage.setItem(UDC_QUERY_KEY, JSON.stringify(existing.filter(r => r.query !== q)));
    } catch {}
    setUdcHistory(prev => prev.filter(r => r.query !== q));
  };

  // ── Очистити всю історію ───────────────────────────────────
  const clearUdcHistory = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { localStorage.removeItem(UDC_QUERY_KEY); } catch {}
    setUdcHistory([]);
    setShowHistory(false);
  };

  // ── Очистити поле вводу ────────────────────────────────────
const handleClear = () => {
    setSearchInput('');
    setSearchResults(null);
    setKwPage(1);
    loadUdcHistory('');
};

  // ── Вибрати елемент з історії і одразу запустити пошук ────
  const selectUdcHistoryItem = (item) => {
    const val  = item.query;
    const mode = item.mode || searchMode;
    setSearchInput(val);
    setSearchMode(mode);
    setShowHistory(false);
    if (mode === 'code') {
      onSearch(val);
    } else {
      doKeywordSearch(val);
    }
  };

  // ── Авто-детект режиму і зміна поля пошуку ────────────────
const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (val.length > 0) {
      if (/^[0-9]/.test(val)) { setSearchMode('code'); setSearchResults(null); }
      else if (/[а-яА-ЯіІїЇєЄёЁa-zA-Z]/.test(val)) setSearchMode('keyword');
    }
    loadUdcHistory(val);
  };
  // ── Перемикання режиму через дропдаун ─────────────────────
const handleModeChange = (mode) => {
    setSearchMode(mode);
    setSearchModeOpen(false);
    setShowHistory(false);
    if (mode === 'code') setSearchResults(null);
  };

  // ── Об'єднаний обробник пошуку ────────────────────────────
const handleUnifiedSearch = (e) => {
  e.preventDefault();
  const val = searchInput.trim();
  if (!val) return;
  saveUdcQuery(val, searchMode);
  setShowHistory(false);
  if (searchMode === 'code') {
    onSearch(val);
  } else {
    doKeywordSearch(val);
  }
};

  // ── Рендер хлібних крихт ──────────────────────────────────
  const renderBreadcrumb = () => (
    <div className="udc-breadcrumb">
      <button
        className="udc-breadcrumb-btn"
        onClick={() => navigateToBreadcrumb(-1)}
        title={t(lang, 'udc_back_to_root_title')}
      >
        <i className="fa-solid fa-house udc-breadcrumb-home-icon" aria-hidden="true" />
        {t(lang, 'udc_root')}
      </button>
      {stack.map((item, idx) => (
        <React.Fragment key={idx}>
          <span className="udc-breadcrumb-sep">›</span>
          {idx < stack.length - 1 ? (
            <button className="udc-breadcrumb-btn" onClick={() => navigateToBreadcrumb(idx)}>
              {item.code}
            </button>
          ) : (
            <span className="udc-breadcrumb-current">{item.code}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ── Рендер панелі поточного вузла (якщо є) ────────────────
  const renderCurrentPanel = () => {
    if (stack.length === 0) return null;
    const current = stack[stack.length - 1];
    const topCls = UDC_CLASSES[stack[0].code] || null;
    return (
      <div className="udc-current-panel" style={topCls ? { '--panel-color': topCls.color } : {}}>
        {topCls && <i className={`${topCls.icon} udc-current-panel-icon`} />}
        <span className="udc-current-code">{current.code}</span>
        {current.label && <span className="udc-current-label">{current.label}</span>}
        {total > 0 && (
          <span className="udc-current-count">
            {total} {t(lang, 'udc_records')}
          </span>
        )}
        <button className="udc-current-search-btn" onClick={() => handleNodeSearch(current.code)}>
          <i className="fa-solid fa-magnifying-glass me-1" />
          {t(lang, 'udc_search_here')}
        </button>
      </div>
    );
  };

const renderKwPagination = (posClass) => (
    <div className={`udc-kw-pag-wrap${posClass ? ' ' + posClass : ''}`}>
      <div className="pagination-block">
        <div className="pagination-controls">
          <button
            className="pagination-btn pagination-btn--nav"
            disabled={kwPage === 1}
            onClick={() => { setKwPage(p => p - 1); setKwJumpInput(''); }}
            title={t(lang, 'prev')}
          >
            <i className="fa-solid fa-chevron-left" />
            <span className="pagination-nav-label">{t(lang, 'prev')}</span>
          </button>
          {getKwPages().map((p, i) =>
            p === '...'
              ? <span key={`e${i}`} className="pagination-ellipsis">…</span>
              : <button
                  key={p}
                  className={`pagination-page-btn${p === kwPage ? ' pagination-page-btn--active' : ''}`}
                  disabled={p === kwPage}
                  onClick={() => { setKwPage(p); setKwJumpInput(''); }}
                >
                  {p}
                </button>
          )}
          <button
            className="pagination-btn pagination-btn--nav"
            disabled={kwPage >= kwTotalPages}
            onClick={() => { setKwPage(p => p + 1); setKwJumpInput(''); }}
            title={t(lang, 'next')}
          >
            <span className="pagination-nav-label">{t(lang, 'next')}</span>
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
        <div className="pagination-jumper">
          <span className="pagination-jumper-label">{t(lang, 'page_label')}</span>
          <input
            type="number" min="1" max={kwTotalPages}
            placeholder={String(kwPage)}
            className="pagination-jump-input"
            aria-label={t(lang, 'page_label')}
            value={kwJumpInput}
            onChange={e => setKwJumpInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleKwJump()}
          />
          <span className="pagination-jumper-label">{t(lang, 'of')} {kwTotalPages}</span>
          <button className="pagination-jump-btn" disabled={!kwJumpInput} onClick={handleKwJump}>
            {t(lang, 'go_btn')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="udc-page">
      <div className="udc-container">
      {/* Кнопка повернення */}
<button className="udc-back-btn" onClick={onBack}>
  <i className="fa-solid fa-arrow-left" />
  {backLabel}
</button>

      {/* Заголовок */}
      <div className="udc-header">
        <i className="fa-solid fa-sitemap udc-header-icon" />
        <h2 className="udc-header-title">{t(lang, 'udc_title')}</h2>
      </div>

{/* Об'єднаний рядок пошуку */}
      <div className="udc-search-bar-wrap">
        <div className="udc-search-bar" ref={searchBarRef}>
        <div
            className={`udc-mode-dropdown${searchModeOpen ? ' udc-mode-dropdown--open' : ''}`}
            ref={modeDropdownRef}
          >
            {/* Планшет/десктоп (>767.98px) — кастомний дропдаун */}
            <button
              type="button"
              className="udc-mode-trigger"
              onClick={() => { setSearchModeOpen(v => !v); setShowHistory(false); }}
              aria-haspopup="listbox"
              aria-expanded={searchModeOpen}
            >
              <span className="udc-mode-label">
                {searchMode === 'code' ? t(lang, 'udc_mode_code') : t(lang, 'udc_mode_kw')}
              </span>
              <i className="fa-solid fa-chevron-down udc-mode-chevron" aria-hidden="true" />
            </button>
            {searchModeOpen && (
              <ul className="udc-mode-menu" role="listbox">
                <li
                  role="option"
                  aria-selected={searchMode === 'code'}
                  className={`udc-mode-item${searchMode === 'code' ? ' udc-mode-item--active' : ''}`}
                  onClick={() => handleModeChange('code')}
                >
                  {t(lang, 'udc_mode_code')}
                  {searchMode === 'code' && <i className="fa-solid fa-check ms-2" aria-hidden="true" />}
                </li>
                <li
                  role="option"
                  aria-selected={searchMode === 'keyword'}
                  className={`udc-mode-item${searchMode === 'keyword' ? ' udc-mode-item--active' : ''}`}
                  onClick={() => handleModeChange('keyword')}
                >
                  {t(lang, 'udc_mode_kw')}
                  {searchMode === 'keyword' && <i className="fa-solid fa-check ms-2" aria-hidden="true" />}
                </li>
              </ul>
            )}

            {/* Мобіл (≤767.98px) — справжній <select>, тап відкриває
                нативний пікер ОС (той самий прийом, що й LangDropdown) */}
            <select
              className="udc-mode-native-select"
              value={searchMode}
              onChange={e => handleModeChange(e.target.value)}
              aria-label={t(lang, 'udc_mode_select_aria')}
            >
              <option value="code">{t(lang, 'udc_mode_code')}</option>
              <option value="keyword">{t(lang, 'udc_mode_kw')}</option>
            </select>
            <i className="fa-solid fa-chevron-down udc-mode-native-chevron" aria-hidden="true" />
          </div>
<form className="udc-search-form" onSubmit={handleUnifiedSearch}>
            
            <div className="udc-input-wrap">
              <input
                type="text"
                className="udc-search-input"
                placeholder={searchMode === 'code' ? t(lang, isNarrowMobile ? 'udc_input_placeholder_short' : 'udc_input_placeholder') : t(lang, 'udc_kw_placeholder')}
                value={searchInput}
                onChange={handleSearchInputChange}
                onFocus={() => { loadUdcHistory(searchInput); setShowHistory(true); }}
              />
              {searchInput && (
                <button type="button" className="udc-search-clear" onClick={handleClear} aria-label={t(lang, 'udc_clear_input')}>
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="udc-search-submit"
              disabled={!searchInput.trim() || searchLoading}
            >
              {searchLoading
                ? <span className="udc-kw-spinner" />
                : <i className="fa-solid fa-magnifying-glass" />}
            </button>
          </form>
          {showHistory && udcHistory.length > 0 && (
            <ul className="udc-history-list" role="listbox">
              <li className="search-autocomplete-header" role="presentation">
                <span className="search-autocomplete-header-label">
                  <i className="fa-solid fa-clock-rotate-left me-1" />{t(lang, 'udc_history_label')}
                </span>
                <button
                  className="search-autocomplete-clear-all"
                  onMouseDown={clearUdcHistory}
                  title={t(lang, 'udc_history_clear_all_title')}
                >
                  {t(lang, 'udc_history_clear_all')}
                </button>
              </li>
              {udcHistory.map((item, i) => (
                <li
                  key={i}
                  role="option"
                  aria-selected={false}
                  className="search-autocomplete-item"
                  onMouseDown={e => { e.preventDefault(); selectUdcHistoryItem(item); }}
                >
                  <i className={`fa-solid ${item.mode === 'code' ? 'fa-hashtag' : 'fa-clock-rotate-left'} search-autocomplete-icon`} />
                  <span className="search-autocomplete-text">{item.query}</span>
                  {item.date && <span className="search-autocomplete-date">{item.date}</span>}
                  <button
                    className="search-autocomplete-delete"
                    onMouseDown={e => deleteUdcHistoryItem(e, item.query)}
                    title={t(lang, 'query_remove_title')}
                    aria-label={`${t(lang, 'query_remove_aria_prefix')} «${item.query}»`}
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
{searchMode === 'keyword' && (
        <div className="udc-search-hint-row">
          <p className="udc-search-hint">{t(lang, 'udc_kw_hint')}</p>
          <div className="udc-options-wrap" ref={optionsRef}>
            <button
              type="button"
              className={`udc-options-btn${(kwLogic === 'and' || !kwStem) ? ' udc-options-btn--active' : ''}`}
              onClick={() => setKwOptionsOpen(v => !v)}
              title={t(lang, 'udc_options_title')}
            >
              <i className="fa-solid fa-sliders" />
            </button>
            {kwOptionsOpen && (
              <div className="udc-options-menu">
                <div className="udc-options-group">
                  <div className="udc-options-group-label">{t(lang, 'udc_options_logic')}</div>
                  <label className="udc-options-item">
                    <input type="radio" name="udc-logic" value="or"
                      checked={kwLogic === 'or'} onChange={() => setKwLogic('or')} />
                    {t(lang, 'udc_logic_or')}
                  </label>
                  <label className="udc-options-item">
                    <input type="radio" name="udc-logic" value="and"
                      checked={kwLogic === 'and'} onChange={() => setKwLogic('and')} />
                    {t(lang, 'udc_logic_and')}
                  </label>
                </div>
                <div className="udc-options-sep" />
                <div className="udc-options-group">
                  <div className="udc-options-group-label">{t(lang, 'udc_options_stem')}</div>
                  <label className="udc-options-item">
                    <input type="radio" name="udc-stem" value="flex"
                      checked={kwStem} onChange={() => setKwStem(true)} />
                    {t(lang, 'udc_stem_flex')}
                  </label>
                  <label className="udc-options-item">
                    <input type="radio" name="udc-stem" value="exact"
                      checked={!kwStem} onChange={() => setKwStem(false)} />
                    {t(lang, 'udc_stem_exact')}
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
      {/* Результати пошуку за ключовими словами */}
      {(searchResults !== null || searchLoading) && (
        <div className="udc-search-bar-results">
          {searchLoading
            ? <div className="udc-kw-results">
                {[52, 72, 44, 63].map((pct, i) => (
                  <div key={i} className="udc-kw-item udc-kw-skeleton">
                    <span className="udc-skeleton-block udc-skeleton-code" />
                    <span className="udc-skeleton-block udc-skeleton-label" style={{ maxWidth: `${pct}%` }} />
                  </div>
                ))}
              </div>
            : searchResults.length === 0
            ? <>
                {renderBreadcrumb()}
                <div className="udc-kw-empty">{t(lang, 'udc_kw_empty')}</div>
              </>
: <>
                {renderBreadcrumb()}
                <div className="udc-kw-results-header">
                  <span className="udc-kw-results-count">
                    {t(lang, 'udc_kw_found')}: <strong>{searchResults.length}</strong>
                  </span>
                  <button type="button" className="udc-kw-reset-btn" onClick={handleClear}>
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                    {t(lang, 'udc_reset_search')}
                  </button>
                </div>
                {kwTotalPages > 1 && renderKwPagination('')}
                <div className="udc-kw-results">
                  {kwDisplayed.map(r => (
                    <div key={r.code} className="udc-kw-item">
                      <button className="udc-kw-item-code" onClick={() => handleNodeSearch(r.code)}>
                        {r.code}
                      </button>
                      <div className="udc-kw-item-body">
                        <span className="udc-kw-item-label">{r.label}</span>
                        {(r.see_also || []).length > 0 && (
                          <div className="udc-kw-see-also">
                            <span className="udc-kw-see-also-label">{t(lang, 'udc_kw_see_also_label')}</span>
                            {r.see_also.map(code => (
                              <button key={code} className="udc-kw-see-also-code" onClick={() => handleNodeSearch(code, true)}>
                                {code}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        className="udc-node-search-btn"
                        onClick={() => handleNodeSearch(r.code)}
                        title={t(lang, 'udc_search_here')}
                      >
                        <i className="fa-solid fa-magnifying-glass" />
                      </button>
                    </div>
                  ))}
                </div>
                {kwTotalPages > 1 && renderKwPagination('pagination-bottom')}
              </>
          }
        </div>
      )}
      {!showingKwResults && (<>
      {/* Хлібні крихти */}
      {renderBreadcrumb()}
      {/* Кнопка «один рівень вище» */}
      {stack.length > 0 && (
        <button
          className="udc-nav-back"
          onClick={() => navigateToBreadcrumb(stack.length - 2)}
        >
          <i className="fa-solid fa-arrow-left" />
           {t(lang, 'udc_back')}
        </button>
      )}

      {/* Панель поточного вузла */}
      {renderCurrentPanel()}

      {/* Стан завантаження / помилка / сітка вузлів */}
      {loading ? (
        stack.length === 0 ? (
          <div className="udc-classes-grid">
            {[0,1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="udc-class-card udc-class-card--skeleton" style={{ '--i': i }}>
                <div className="udc-class-card-main">
                  <div className="udc-class-card-icon-wrap" />
                  <div className="udc-class-card-body">
                    <span className="udc-skeleton-block" style={{ width: 20, height: 11, borderRadius: 3 }} />
                    <span className="udc-skeleton-block" style={{ width: '65%', height: 14, borderRadius: 3, marginTop: 5 }} />
                    <span className="udc-skeleton-block" style={{ width: 55, height: 11, borderRadius: 3, marginTop: 5 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="udc-list">
            {[60, 45, 78, 52, 67, 38].map((pct, i) => (
              <div key={i} className="udc-node-row udc-node-skeleton">
                <span className="udc-skeleton-block udc-skeleton-check" style={{ margin: '0 4px 0 14px' }} />
                <div className="udc-node-row-main">
                  <i className="fa-solid fa-folder udc-node-folder" style={{ opacity: 0.18 }} />
                  <span className="udc-skeleton-block udc-skeleton-code" />
                  <span className="udc-skeleton-block udc-skeleton-label" style={{ maxWidth: `${pct}%` }} />
                  <span className="udc-skeleton-block udc-skeleton-count" />
                </div>
                <div style={{ width: 40, flexShrink: 0 }} />
              </div>
            ))}
          </div>
        )
      ) : error ? (
        <div className="udc-error">{error}</div>
      ) : nodes.length === 0 ? (
        <div className="udc-empty">
          {stack.length > 0
            ? t(lang, 'udc_no_subnodes')
            : (
              <>
                <i className="fa-solid fa-spinner fa-spin udc-empty-spinner" aria-hidden="true" />
                {' '}{t(lang, 'udc_empty')}
              </>
            )}
        </div>
      ) : stack.length === 0 ? (
        <div className="udc-classes-grid">
          {nodes.map((node, idx) => {
            const cls = UDC_CLASSES[node.code] || { icon: 'fa-solid fa-folder', color: '#64748b' };
            return (
              <div
                key={node.code}
                className={`udc-class-card${selectedCodes.has(node.code) ? ' udc-class-card--selected' : ''}`}
                style={{ '--card-color': cls.color, '--i': idx }}
              >
                <input
                  type="checkbox"
                  className="udc-class-check"
                  checked={selectedCodes.has(node.code)}
                  onChange={() => toggleCode(node.code)}
                  aria-label={node.code}
                />
                <button
                  className="udc-class-card-main"
                  onClick={() => navigateTo(node.code, node.label)}
                >
                  <div className="udc-class-card-icon-wrap">
                    <i className={`${cls.icon} udc-class-card-icon`} />
                  </div>
                  <div className="udc-class-card-body">
                    <span className="udc-class-card-code">{node.code}</span>
                    {node.label && <span className="udc-class-card-label">{node.label}</span>}
                    {node.count > 0 && (
                      <span className="udc-class-card-count">{node.count} {t(lang, 'udc_count_label')}</span>
                    )}
                  </div>
                  <i className="fa-solid fa-chevron-right udc-class-card-arrow" />
                </button>
                <button
                  className="udc-class-card-search"
                  onClick={() => handleNodeSearch(node.code)}
                  title={t(lang, 'udc_search_here')}
                >
                  <i className="fa-solid fa-magnifying-glass" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="udc-list">
          {nodes.map((node, idx) => (
            <div key={node.code} className={`udc-node-row${selectedCodes.has(node.code) ? ' udc-node-row--selected' : ''}`} style={{ '--i': idx }}>
              <input
                type="checkbox"
                className="udc-node-check"
                checked={selectedCodes.has(node.code)}
                onChange={() => toggleCode(node.code)}
                aria-label={node.code}
              />
              <button
                className="udc-node-row-main"
                onClick={() => navigateTo(node.code, node.label)}
              >
                <i className="fa-solid fa-folder udc-node-folder" />
                <span className="udc-node-code">{node.code}</span>
                {node.label && (
                  <span className="udc-node-label">{node.label}</span>
                )}
                {node.count > 0 && (
                  <span className="udc-node-count">{node.count} {t(lang, 'udc_count_label')}</span>
                )}
              </button>
              <button
                className="udc-node-search-btn"
                onClick={() => handleNodeSearch(node.code)}
                title={t(lang, 'udc_search_here')}
              >
                <i className="fa-solid fa-magnifying-glass" />
              </button>
            </div>
          ))}
        </div>
      )}
      {selectedCodes.size > 0 && (
        <div className="udc-selection-bar">
          <span className="udc-selection-count">
            <i className="fa-solid fa-check-square me-1" aria-hidden="true" />
            {selectedCodes.size}
          </span>
          <button className="udc-selection-search-btn" onClick={handleSelectedSearch}>
            <i className="fa-solid fa-magnifying-glass me-1" aria-hidden="true" />
            {t(lang, 'udc_selected_search')}
          </button>
          <button
            className="udc-selection-clear-btn"
            onClick={() => setSelectedCodes(new Set())}
            title={t(lang, 'udc_selected_clear')}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
      )}
      </>)}
      </div>
    </div>
  );
}
